import {
  mapImage,
  mapText,
  mapTitle,
  mapVideo,
  pickStr,
  mapOverlayOpacity,
  mapOverlayDirection,
} from "@/components/DesignSystem/utils/teaserHelper"
import { validateFullBleedExperienceProps } from "../validators/fullBleedExperienceValidator"

/**
 * Maps an AEM “full bleed experience” container into KDS props.
 *
 * Expects the first item in `:itemsOrder` to be `fullproductdisplay` and maps:
 * - `mod` (defaults to `"white"`)
 * - `heading` via `mapTitle`
 * - `image` via `mapImage` (prefers `media.image`, falls back to `content.image`)
 * - `video` via `mapVideo` (if present)
 * - `copy` via `mapText` (omitted if empty)
 * - `textPlacement` only if one of: `"bottom-middle" | "top-middle"`
 *
 * @param {Record<string, any>} container Raw AEM container node.
 * @return {Record<string, any> | null} Mapped props object or `null` if nothing to map.
 */
export function mapFullBleedExperienceProps(container) {
  try {
    if (!container) return null
    const node =
      container?.[":itemsOrder"]
        ?.map(() => container?.[":items"]?.["fullproductdisplay"])
        ?.filter(Boolean) || []

    if (!node || !node.length) return null

    const basic = node?.[0]?.basic
    const media = node?.[0]?.media
    const heading = mapTitle(basic?.heading)
    const copy = mapText(basic?.copy)
    const image = mapImage(basic?.image)
    const mergedVideo = {
      ...(media?.video && typeof media.video === "object" ? media.video : {}),
      ...(basic?.video && typeof basic.video === "object" ? basic.video : {}),
    }
    const video = mapVideo(mergedVideo)
    const mod = pickStr(basic?.mod) || "white"
    const hasPhotoOverlay = basic?.hasPhotoOverlay
    const gradientDirection = mapOverlayDirection(basic?.gradientDirection)
    const opacity = mapOverlayOpacity(basic?.opacity)

    const textPlacement = (() => {
      const raw = pickStr(basic?.textPlacement)
      const ok = new Set(["bottom-middle", "top-middle"])
      return raw && ok.has(raw) ? raw : undefined
    })()

    if (heading) {
      heading.reveal = { enabled: true, delay: 300 }
    }

    if (copy) {
      copy.reveal = { enabled: true, delay: 600 }
    }

    if (mod !== "black" && mod !== "charcoal") {
      if (heading) {
        heading.textColor = "black"
      }
      if (copy) {
        copy.textColor = "black"
      }
    }
    if (video) {
      video.loop = Boolean(video.autoPlay)
    }

    const fullBleedExperienceProps = {
      ...(mod ? { mod } : {}),
      ...(heading ? { heading } : {}),
      ...(image ? { image } : {}),
      ...(video ? { video } : {}),
      ...(copy ? { copy } : {}),
      ...(textPlacement ? { textPlacement } : {}),
      ...(hasPhotoOverlay ? { hasPhotoOverlay } : {}),
      ...(gradientDirection ? { gradientDirection } : {}),
      ...(opacity ? { opacity } : {}),
    }
    const { isValid, errors } = validateFullBleedExperienceProps(
      fullBleedExperienceProps
    )

    if (!isValid) {
      console.warn("fullBleedExperienceProps validation errors:", errors)
      return null
    }

    return fullBleedExperienceProps
  } catch (error) {
    console.error("FullBleedExperience mapping error:", error)
    return null
  }
}
